/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { getContentId, useCurrentQueueItem, usePlayer } from "@packages/media";
import { useLocalStorageState, useRequestTranscript } from "@packages/sdk";
import type { ThemeType, WithButtonWrapper } from "@packages/ui";
import {
  colors,
  cssMediaQueryMinWidthTablet,
  emotionDynamics,
  FullscreenModal,
  getRGBAFromColor,
  IconButton,
  spacing,
  TextOnScreenIcon,
  useTheme,
  zIndices,
} from "@packages/ui";
import type { MouseEventHandler } from "react";
import { useContext, useState } from "react";

import { FragmentAsChild } from "../../primitives/FragmentAsChild";
import { PlayerInternalContext } from "../MediaPlayer.emotion";
import { TextOnScreenContent } from "./TextOnScreenContent.emotion";

const modalStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  padding: spacing.none,
  zIndex: zIndices.transcript,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.none,
    paddingRight: spacing.none,
  },
});
const establishBackgroundStyle: Interpolation<string> = (bgColor) => ({
  "special-color": bgColor,
});
const hiddenTriggerStyle: Interpolation = {
  background: colors.transparent,
  border: spacing.none,
  height: spacing.none,
  outline: spacing.none,
  overflow: "hidden",
  padding: spacing.none,
  pointerEvents: "none",
  position: "absolute",
  width: spacing.none,
};

// we put a simple z-index here because the layer context of this button is INSIDE the fullscreen-modal,
// so we only need it on top of its parent
const defaultCloseStyle: Interpolation = {
  left: spacing.m,
  [cssMediaQueryMinWidthTablet]: { left: "80px", right: "80px" },
  opacity: 1,
  pointerEvents: "auto",
  position: "fixed",
  top: spacing.m,
  transition: "opacity 250ms ease-out",
  zIndex: zIndices.maskOver,
};

export type TextOnScreenProps = WithButtonWrapper<{
  id?: number;
  prayerId?: number;
  iconColor?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}>;

export const OldTextOnScreen = ({
  id,
  prayerId,
  iconColor,
  Wrapper = FragmentAsChild,
  onClick,
  open,
  onOpenChange,
  className = "",
}: TextOnScreenProps) => {
  const {
    query: { data: transcriptData },
  } = useRequestTranscript({ id });
  const analytics = useAnalytics();
  const t = useTranslations();
  const { popupRef } = useContext(PlayerInternalContext);
  const { tosTrigger } = usePlayer();

  const [immersive, setImmersive] = useLocalStorageState<
    "immersive" | "default"
  >({ key: "tos-immersive", defaultValue: "default" });
  const [scrollStage, setScrollStage] = useState<0 | 1>(0);
  const { currentItem } = useCurrentQueueItem();
  const player = usePlayer();
  const { currentTheme } = useTheme();

  if (!transcriptData) return null;

  const { transcript, images } = transcriptData;

  const handleReport = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedReportATranscriptConcern,
      properties: {
        prayer_id: prayerId,
        audio_id: id,
      },
    });
    window.open("https://help.hallow.com");
  };

  if (!transcript?.length)
    return (
      <Wrapper asChild>
        <IconButton
          variant={"transparentWhite"}
          icon={<TextOnScreenIcon fill={iconColor ? iconColor : null} />}
          disabled={true}
          title={t("no_transcript_available_text")}
        />
      </Wrapper>
    );

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (open) {
      analytics.track({
        event: HallowAnalyticsEvent.TappedTextOnScreen,
        properties: {
          content_id: getContentId(currentItem),
          content_type: currentItem.type,
          has_transcript: !!transcriptData?.transcript,
          is_video: currentItem.selected_audio.media_type === "video",
        },
      });
    }
  };

  return (
    <FullscreenModal
      trigger={
        <Wrapper asChild>
          <IconButton
            ref={tosTrigger}
            variant={"transparentWhite"}
            icon={<TextOnScreenIcon fill={iconColor ? iconColor : null} />}
            onClick={onClick}
            title={t("text_on_screen_read_along")}
            tabIndex={0}
            css={
              currentItem?.selected_audio?.media_type === "video"
                ? hiddenTriggerStyle
                : undefined
            }
          />
        </Wrapper>
      }
      removeOverlay
      open={open}
      onOpenChange={handleOpenChange}
      modal={true}
      contentProps={{
        onInteractOutside: (e) => e.preventDefault(),
        className,
      }}
      closeCSS={defaultCloseStyle}
      contentCSS={[
        modalStyle,
        immersive === "immersive"
          ? emotionDynamics.backgroundColorFromRGBAObject(
              getRGBAFromColor({
                color: images.color_hex ?? "#000000",
                format: "object",
              }),
            )
          : null,
        immersive === "immersive"
          ? establishBackgroundStyle(images.color_hex ?? "#000000")
          : null,
      ]}
      portalProps={{ container: popupRef.current }}
    >
      <TextOnScreenContent
        immersive={immersive}
        setImmersive={setImmersive}
        scrollStage={scrollStage}
        setScrollStage={setScrollStage}
        audioId={id}
        onReport={handleReport}
        playing={
          currentItem?.type === "prayer" &&
          currentItem.selected_audio.id === id &&
          !!player &&
          !player.paused
        }
        play={() => player?.playNew({ type: "audio", id })}
        isCurrentItem={
          currentItem?.type === "prayer" && currentItem.selected_audio.id === id
        }
        t={t}
        theme={currentTheme}
      />
    </FullscreenModal>
  );
};
